import type { ChatMessage } from '../../services/chatService';
import { eventService } from '../../services/eventService';

import { api } from './api';

export interface RewardOffer {
  id: string;
  overview_entry_id: string;
  project_id: string;
  session_id: string | null;
  run_group: string | null;
  amount: string;
  reason: string;
  original_text: string;
  claimed_at: string | null;
  can_claim: boolean;
  claim_message_id: string | null;
  beneficiary_user_id?: string;
  created_at?: string;
}

export interface RewardClaimResult {
  /** True when this request only returns an earlier successful claim. */
  already_claimed?: boolean;
  reward: RewardOffer;
  credits_balance: number;
  message?: ChatMessage | null;
}

export interface RewardClaimBatchResult {
  /** True when every requested reward had already been claimed. */
  already_claimed?: boolean;
  rewards: RewardOffer[];
  credits_balance: number;
  message?: ChatMessage | null;
}

const rewardTags = (projectId: string) => [{ type: 'Reward' as const, id: projectId }];

const mergeClaimed = (offers: RewardOffer[], claimed: RewardOffer[]): void => {
  claimed.forEach((reward) => {
    const index = offers.findIndex((offer) => offer.id === reward.id);

    if (index >= 0) offers[index] = reward;
    else offers.push(reward);
  });
};

const announceClaim = (result: { credits_balance: number; message?: ChatMessage | null }): void => {
  // This is a local event for the claimant's authenticated HTTP response.
  eventService.emit('credits_update', { credits_balance: result.credits_balance });
  if (result.message) eventService.emit('chat_message', { type: 'chat_message', ...result.message });
};

export const rewardsApi = api.injectEndpoints({
  // eslint-disable-next-line max-lines-per-function
  endpoints: (builder) => ({
    // One project cache serves every activity bubble, historical entry and reminder.
    getRewards: builder.query<RewardOffer[], string>({
      query: (projectId) => `/api/v1/projects/${projectId}/rewards`,
      providesTags: (_result, _error, projectId) => rewardTags(projectId),
      // eslint-disable-next-line max-statements
      async onCacheEntryAdded(projectId, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        let loaded = false;
        let pendingRefresh = false;
        const refresh = () => {
          if (loaded) dispatch(api.util.invalidateTags(rewardTags(projectId)));
          else pendingRefresh = true;
        };
        const onReward = (payload: unknown) => {
          const event = payload as { reward?: RewardOffer };

          if (event.reward?.project_id === projectId) refresh();
        };
        const unsubscribe = [
          eventService.subscribe('reward_offered', onReward),
          eventService.subscribe('reward_updated', onReward),
          eventService.subscribe('connected', refresh),
        ];

        try {
          await cacheDataLoaded;
          loaded = true;
          if (pendingRefresh) refresh();
          await cacheEntryRemoved;
        } catch { /* An unmounted or failed initial query needs no listener. */ }
        finally { unsubscribe.forEach((stop) => stop()); }
      },
    }),
    claimReward: builder.mutation<RewardClaimResult, {
      projectId: string; rewardId: string; sessionId?: string | null;
    }>({
      query: ({ projectId, rewardId, sessionId }) => ({
        url: `/api/v1/projects/${projectId}/rewards/${rewardId}/claim`, method: 'POST',
        body: sessionId ? { session_id: sessionId } : {},
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(rewardsApi.util.updateQueryData('getRewards', projectId, (offers) => mergeClaimed(offers, [data.reward])));
          announceClaim(data);
        } catch { /* The shared mutation state renders the claim error and Retry. */ }
      },
      invalidatesTags: (_result, _error, { projectId }) => rewardTags(projectId),
    }),
    // A grouped claim is one request, one balance update, one receipt and one refresh.
    claimRewards: builder.mutation<RewardClaimBatchResult, {
      projectId: string; rewardIds: string[]; sessionId?: string | null;
    }>({
      query: ({ projectId, rewardIds, sessionId }) => ({
        url: `/api/v1/projects/${projectId}/rewards/claim`, method: 'POST',
        body: { reward_ids: rewardIds, ...(sessionId ? { session_id: sessionId } : {}) },
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(rewardsApi.util.updateQueryData('getRewards', projectId, (offers) => mergeClaimed(offers, data.rewards)));
          announceClaim(data);
        } catch { /* The shared batch state renders the claim error and Retry. */ }
      },
      invalidatesTags: (_result, _error, { projectId }) => rewardTags(projectId),
    }),
  }),
});

export const { useGetRewardsQuery, useClaimRewardMutation, useClaimRewardsMutation } = rewardsApi;
